import { Show } from "shared/components";
import {
  SelfReportedIncomeSource,
  SelfReportedVerificationItem,
  VerificationMethodType
} from "types/landlord/reports";
import { formatToCurrency } from "utils/currency";
import { AssetItem } from "views/applications-id-report/components/SmartRender/AssetItem";
import { UniversalExpanderDataColumn } from "views/applications-id-report/slices/IncomeReportSlice/components/UniversalExpanderDataColumn";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import { cx } from "shared/utils";
import { getSelfReportedIncomeMappings } from "../VerifiedIncomeSourcesTableExpander/utils";

interface SelfReportedIncomeSourcesTableExpanderProps {
  rowData: SelfReportedVerificationItem;
}

const mapMethods = {
  [VerificationMethodType.BankStatement]: "bank_statement_files",
  [VerificationMethodType.PayStub]: "pay_stub_files",
  [VerificationMethodType.Other]: "other_files"
};

export const SelfReportedIncomeSourcesTableExpander = ({
  rowData
}: SelfReportedIncomeSourcesTableExpanderProps) => {
  const selfReportedIncomeData = getSelfReportedIncomeMappings(rowData);

  const currentTypeOfSelfAttested = rowData.income_source
    .toLowerCase()
    .replaceAll(/[ -]+/g, "_");

  const assetType = rowData.income_source;
  const typeOfMethod =
    rowData[currentTypeOfSelfAttested]?.verification_method?.verification_type;

  const isOfferletter = assetType === SelfReportedIncomeSource.OfferLetter;

  const assetPath = isOfferletter
    ? rowData.offer_letter?.offer_letter_file?.asset
    : typeOfMethod
      ? rowData[currentTypeOfSelfAttested]?.verification_method[
          mapMethods[typeOfMethod]
        ]
      : null;

  return (
    <div className="px-6 py-8">
      <div className="flex flex-col gap-2">
        <p className="text-content-700 font-medium text-xs">
          Gross monthly income
        </p>
        <p className="text-2xl font-bold text-content-1000">
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {rowData.monthly_income?.cents
            ? formatToCurrency(rowData.monthly_income)
            : "--"}{" "}
        </p>
      </div>

      <div className="mt-9">
        <Show if={assetPath}>
          <p className="pb-2 border-b border-alpha-40">Documents</p>

          <div
            className={cx(
              "mt-4",
              isOfferletter ? "mb-6" : "mb-9 flex flex-row gap-4 flex-wrap"
            )}
          >
            {isOfferletter ? (
              <AssetItem
                url={assetPath?.url || ""}
                content_type={assetPath?.content_type || ""}
                filename={assetPath?.filename}
                assetName={assetPath?.filename}
              />
            ) : (
              assetPath?.map((el) => {
                return (
                  <AssetItem
                    key={el?.asset?.url}
                    url={el?.asset?.url || ""}
                    content_type={el?.asset?.content_type || ""}
                    filename={el?.asset?.filename}
                    assetName={el?.asset?.filename}
                  />
                );
              })
            )}
          </div>

          <Show if={isOfferletter}>
            <div className="flex items-center gap-4 mb-9">
              <InfoIcon className="text-warning-500 h-5" />
              <p className="text-content-400 font-medium">
                Fraud check not available for offer letter. Please manually
                review the documents
              </p>
            </div>
          </Show>
        </Show>
        <Show if={Boolean(!rowData.other_income)}>
          <UniversalExpanderDataColumn
            title="Employment details"
            data={selfReportedIncomeData.employmentDetailsMappings}
          />
        </Show>
        <Show if={Boolean(rowData.other_income)}>
          <UniversalExpanderDataColumn
            title="Income details"
            data={selfReportedIncomeData.incomeMappings}
          />
        </Show>

        <Show if={Boolean(rowData.employment) || Boolean(rowData.offer_letter)}>
          <UniversalExpanderDataColumn
            title="Supervisor"
            data={selfReportedIncomeData.supervisorMappings}
          />
        </Show>

        <UniversalExpanderDataColumn
          title="Pre-tax income"
          data={selfReportedIncomeData.preTaxIncomeMappings}
        />
      </div>
    </div>
  );
};
